import * as echarts from 'echarts';

/**
 * Framework-agnostic port of the ECharts "option building" subsystem extracted
 * from `SelfServiceVisualizationComponent`. This class produces ECharts options
 * for the chart "builder preview" so a second component (the published
 * dashboard) can produce IDENTICAL options.
 *
 * The logic is a verbatim mechanical port: every method preserves the original
 * algorithm and comments. Only the instance-state references were rewired:
 *   - `this.currentShapeOptions` -> `this.shapeOptions`
 *   - `this.palettes`           -> `this.deps.palettes`
 *   - `this.customAggregations` -> `this.deps.customAggregations`
 *   - `this.builtinAggregations`-> `this.deps.builtinAggregations`
 *   - `this.chartTypes`         -> `this.deps.chartTypes`
 */

// ---------------------------------------------------------------------------
// Types (duplicated from the source component — intentionally NOT imported)
// ---------------------------------------------------------------------------

export type SupportedChartType = string;
export type ColumnType = 'string' | 'number' | 'date' | 'boolean' | 'empty';
export type MappingKey = 'xAxis' | 'yAxis' | 'category' | 'value' | 'xAxis2' | 'yAxis2' | 'size' | 'colorBy';
export type SortDirection = 'asc' | 'desc';
export type TopNDirection = 'top' | 'bottom';
export type DateGranularity = 'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export type SupportedFileType = 'csv' | 'excel' | 'entity';
export type ChartPreviewKind =
  | 'axis-bar'
  | 'axis-line'
  | 'horizontal-bar'
  | 'area'
  | 'stacked-bar'
  | 'stacked-horizontal-bar'
  | 'stacked-line'
  | 'stacked-area'
  | 'bar-line'
  | 'pie'
  | 'donut'
  | 'treemap'
  | 'scatter'
  | 'bubble'
  | 'quadrant-bubble'
  | 'waterfall'
  | 'waterfall-stack'
  | 'gantt'
  | 'kpi'
  | 'table';
export type NumberFormat = 'plain' | 'thousands' | 'percent' | 'currency';
export type LineShape = 'curved' | 'straight';
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right';
export type LabelMode = 'value' | 'percent' | 'name-value' | 'name-percent';
export type AggregationType =
  | 'sum'
  | 'avg'
  | 'count'
  | 'distinctCount'
  | 'min'
  | 'max'
  | 'median'
  | 'range'
  | 'first'
  | 'last'
  | string;

export interface DatasetColumn {
  name: string;
  type: ColumnType;
}

export interface UploadedDataset {
  datasetId?: string;
  fileName: string;
  sourceType: SupportedFileType;
  columns: DatasetColumn[];
  rows: Record<string, string>[];
}

export interface ChartShapeOptions {
  sortBy?: string;
  sortDir?: SortDirection;
  topN?: number | null;
  topNDirection?: TopNDirection;
  dateGranularity?: DateGranularity;
  tooltipColumns?: string[];
  referenceValue?: number | null;
}

export type TooltipTrigger = 'auto' | 'axis' | 'item';
export type TooltipAxisPointer = 'none' | 'line' | 'cross' | 'shadow';

export interface ChartStyle {
  titleOverride: string;
  showLegend: boolean;
  legendPosition: LegendPosition;
  showLabels: boolean;
  labelMode: LabelMode;
  palette: string;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisLabel2?: string;
  yAxisLabel2?: string;
  quadrantTopLeftLabel?: string;
  quadrantTopRightLabel?: string;
  quadrantBottomLeftLabel?: string;
  quadrantBottomRightLabel?: string;
  numberFormat: NumberFormat;
  decimals: number;
  numberFormat2?: NumberFormat;
  decimals2?: number;
  lineShape?: LineShape;
  comparisonValue: number | null;
  tableRowLimit: number;
  tooltipEnabled: boolean;
  tooltipTrigger: TooltipTrigger;
  tooltipBackground: string;
  tooltipTextColor: string;
  tooltipBorderColor: string;
  tooltipBorderRadius: number;
  tooltipFontSize: number;
  tooltipAxisPointer: TooltipAxisPointer;
  tooltipDecimals?: number;
}

export interface CustomAggregation {
  key: string;
  label: string;
  expression: string;
}

/** Dependency state required by the ported option-building methods. */
export interface SelfServiceChartOptionBuilderDeps {
  palettes: Array<{ key: string; label: string; colors: string[] }>;
  customAggregations: Array<{ key: string; label: string; expression: string }>;
  builtinAggregations: Array<{ label: string; value: string }>;
  chartTypes: Array<{
    value: string;
    previewKind: string;
    requiredMappings?: string[];
    supportsAggregation?: boolean;
  }>;
}

export class SelfServiceChartOptionBuilder {
  /** Replaces every `this.currentShapeOptions` reference from the source. */
  shapeOptions: ChartShapeOptions | null = null;
  // The title that will actually render for the current build (style.titleOverride
  // || titleText). Legend/grid spacing must treat the chart as titled even with
  // no explicit override, else a top legend overlaps the title.
  private currentTitleText = '';

  constructor(private readonly deps: SelfServiceChartOptionBuilderDeps) {}

  applyDateGranularityToDataset(dataset: UploadedDataset, xKey: string): UploadedDataset {
    const granularity = this.shapeOptions?.dateGranularity;
    if (!granularity || granularity === 'none' || !xKey) return dataset;
    const xColumn = dataset.columns.find((c) => c.name === xKey);
    if (!xColumn || xColumn.type !== 'date') return dataset;
    const bucket = (raw: string): string => {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      switch (granularity) {
        case 'year': return String(y);
        case 'quarter': return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
        case 'month': return `${y}-${String(m).padStart(2, '0')}`;
        case 'week': {
          const start = new Date(d);
          start.setDate(d.getDate() - d.getDay());
          return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        }
        case 'day':
        default: return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    };
    return {
      ...dataset,
      rows: dataset.rows.map((row) => ({ ...row, [xKey]: bucket(row[xKey] || '') })),
    };
  }

  applyShapeToAggregated(
    items: Array<{ name: string; value: number }>,
  ): Array<{ name: string; value: number }> {
    const opts = this.shapeOptions;
    if (!opts) return items;
    let result = items;
    if (opts.sortBy) {
      const dir = opts.sortDir === 'desc' ? -1 : 1;
      result = [...result].sort((a, b) => {
        // sortBy === valueKey â†’ sort by value, else by name
        const useValue = opts.sortBy !== 'name' && (a.value !== undefined);
        if (useValue) return (a.value - b.value) * dir;
        return a.name.localeCompare(b.name) * dir;
      });
    }
    if (opts.topN && opts.topN > 0) {
      const sortedForLimit = opts.sortBy
        ? result
        : [...result].sort((a, b) => b.value - a.value);
      result = opts.topNDirection === 'bottom'
        ? sortedForLimit.slice(-opts.topN)
        : sortedForLimit.slice(0, opts.topN);
    }
    return result;
  }

  getPaletteColors(key: string): string[] {
    return (this.deps.palettes.find((p) => p.key === key) || this.deps.palettes[0]).colors;
  }

  formatNumber(value: number, style: ChartStyle): string {
    return this.formatNumberWith(value, style.numberFormat, style.decimals);
  }

  /** Format for the SECONDARY (right) Y-axis (numberFormat2/decimals2, falling
   *  back to primary). MUST mirror the preview builder. */
  formatNumberSecondary(value: number, style: ChartStyle): string {
    return this.formatNumberWith(value, style.numberFormat2 ?? style.numberFormat, style.decimals2 ?? style.decimals);
  }

  /** Tooltip value formatter — axis number format with tooltipDecimals override
   *  (falls back to axis decimals). MUST mirror the preview builder.
   *
   *  Year-like values (a whole number in 1900–2100) are shown raw — a year is an
   *  identifier, not a measure, so thousands/percent/currency must not apply. */
  formatNumberTooltip(value: number, style: ChartStyle, secondary = false): string {
    if (this.isYearLike(value)) {
      return String(value);
    }
    const fmt = secondary ? (style.numberFormat2 ?? style.numberFormat) : style.numberFormat;
    const axisDec = secondary ? (style.decimals2 ?? style.decimals) : style.decimals;
    return this.formatNumberWith(value, fmt, style.tooltipDecimals ?? axisDec);
  }

  /** A whole number in a plausible calendar-year range. Mirrors the preview
   *  builder's data-utils so years skip numeric formatting in tooltips. */
  isYearLike(value: number): boolean {
    return Number.isInteger(value) && value >= 1900 && value <= 2100;
  }

  /** True when EVERY non-blank value in `column` is a year-like number. Mirrors
   *  the preview builder's data-utils. Used to avoid summing a year column on a
   *  value axis. */
  isYearColumn(dataset: UploadedDataset, columnName: string): boolean {
    if (!columnName) return false;
    let sawValue = false;
    for (const row of dataset.rows) {
      const raw = row[columnName];
      if (raw === undefined || raw === null || String(raw).trim() === '') continue;
      if (!this.isYearLike(Number(raw))) return false;
      sawValue = true;
    }
    return sawValue;
  }

  /** Resolve the aggregation for a value column: a year column collapses additive
   *  aggregations to `max` so the bar height stays a real year. MUST mirror the
   *  preview builder's resolveValueAggregation. */
  resolveValueAggregation(
    dataset: UploadedDataset,
    columnName: string,
    aggregation: AggregationType,
  ): AggregationType {
    if (!this.isYearColumn(dataset, columnName)) return aggregation;
    const additive: AggregationType[] = ['sum', 'avg', 'count', 'distinctCount', 'median', 'range'];
    return additive.includes(aggregation) ? 'max' : aggregation;
  }

  /** Compact axis-label formatter — abbreviates large magnitudes to K/M/B so a
   *  tick label never widens the grid past the plot (ECharts containLabel
   *  reserves the widest label's width INSIDE the grid; a raw "2,645,646" or a
   *  percent-formatted "300,000,000%" collapses the plot into a thin strip).
   *  MUST mirror the preview builder's data-utils formatAxisLabel. Tooltips keep
   *  full precision via formatNumberTooltip. */
  formatAxisLabel(value: number, style: ChartStyle, secondary = false): string {
    const fmt = secondary ? (style.numberFormat2 ?? style.numberFormat) : style.numberFormat;
    return this.formatCompact(value, fmt);
  }

  private formatCompact(value: number, numberFormat: NumberFormat): string {
    if (!Number.isFinite(value)) return '—';
    const scaled = numberFormat === 'percent' ? value * 100 : value;
    const abs = Math.abs(scaled);
    const prefix = numberFormat === 'currency' ? '$' : '';
    const suffix = numberFormat === 'percent' ? '%' : '';
    let body: string;
    if (abs >= 1e9) {
      body = this.trimZeros((scaled / 1e9).toFixed(1)) + 'B';
    } else if (abs >= 1e6) {
      body = this.trimZeros((scaled / 1e6).toFixed(1)) + 'M';
    } else if (abs >= 1e3) {
      body = this.trimZeros((scaled / 1e3).toFixed(1)) + 'K';
    } else {
      body = this.trimZeros(scaled.toFixed(abs < 10 && !Number.isInteger(scaled) ? 1 : 0));
    }
    return `${prefix}${body}${suffix}`;
  }

  private trimZeros(s: string): string {
    return s.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }

  private formatNumberWith(value: number, numberFormat: NumberFormat, decimalsRaw: number | undefined): string {
    if (!Number.isFinite(value)) {
      return '—';
    }
    const decimals = Math.max(0, Math.min(6, Math.floor(decimalsRaw || 0)));
    switch (numberFormat) {
      case 'thousands':
        return value.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      case 'percent':
        return `${(value * 100).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}%`;
      case 'currency':
        return value.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      default:
        return value.toFixed(decimals);
    }
  }

  buildChartOption(
    chartType: SupportedChartType,
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    this.currentTitleText = titleText || '';
    const previewKind = this.deps.chartTypes.find((item) => item.value === chartType)?.previewKind || 'axis-bar';

    let option: echarts.EChartsOption;
    switch (previewKind) {
      case 'pie':
        option = this.buildPieOption(false, mapping, aggregation, dataset, style, titleText);
        break;
      case 'donut':
        option = this.buildPieOption(true, mapping, aggregation, dataset, style, titleText);
        break;
      case 'treemap':
        option = this.buildTreemapOption(mapping, aggregation, dataset, style, titleText);
        break;
      case 'scatter':
        option = this.buildScatterOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'bubble':
        option = this.buildBubbleOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'quadrant-bubble':
        option = this.buildQuadrantBubbleOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'horizontal-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { horizontal: true });
        break;
      case 'stacked-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { stacked: true });
        break;
      case 'stacked-horizontal-bar':
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText, { horizontal: true, stacked: true });
        break;
      case 'stacked-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { stacked: true });
        break;
      case 'stacked-area':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { area: true, stacked: true });
        break;
      case 'area':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { area: true });
        break;
      case 'axis-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'step-line':
        option = this.buildAxisChartOption('line', mapping, aggregation, dataset, style, seriesField, titleText, { step: true });
        break;
      case 'bar-line':
        option = this.buildBarLineOption(mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'waterfall':
        option = this.buildWaterfallOption(mapping, aggregation, dataset, style, titleText);
        break;
      case 'waterfall-stack':
        option = this.buildWaterfallStackOption(mapping, aggregation, dataset, style, seriesField, titleText);
        break;
      case 'gantt':
        option = this.buildGanttOption(mapping, dataset, style, seriesField, titleText);
        break;
      case 'axis-bar':
      default:
        option = this.buildAxisChartOption('bar', mapping, aggregation, dataset, style, seriesField, titleText);
        break;
    }

    // Axis names were being clipped at the chart edges (e.g. "Units"/"year" at
    // the top, "Region"/"region" truncated to "R"/"re" at the right) because the
    // ECharts default places value-axis names at 'end' (top) and category-axis
    // names at 'end' (right) with no reserved space. Centre them along their
    // axis and reserve grid room. MUST mirror the preview builder
    // (self-service-chart-option-builder.service.ts) so the published view
    // matches the builder preview.
    this.normalizeAxisNames(option);
    this.normalizeLegendSpacing(option, style);
    // Clamp the accumulated pixel insets so the plot keeps a healthy share of a
    // short card (secondary Y + X2 + top legend + title otherwise crush it into
    // a thin band). MUST mirror the preview builder's clampGridForShortCards.
    return this.clampGridForShortCards(option);
  }

  /** Cap the COMBINED grid insets to a fraction of the reference card so the plot
   *  never collapses on short (~240px) cards. MUST mirror the preview builder. */
  private clampGridForShortCards(option: echarts.EChartsOption): echarts.EChartsOption {
    const opt = option as Record<string, unknown>;
    const grid = opt['grid'] as Record<string, unknown> | undefined;
    if (!grid) return option;
    const REF_H = 240;
    const REF_W = 360;
    const MIN_PLOT_RATIO = 0.5;
    const numeric = (v: unknown, fallback: number): number => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const scaleAxis = (aKey: string, bKey: string, budget: number): void => {
      const a = numeric(grid[aKey], 0);
      const b = numeric(grid[bKey], 0);
      const total = a + b;
      if (total <= budget || total <= 0) return;
      const factor = budget / total;
      grid[aKey] = Math.round(a * factor);
      grid[bKey] = Math.round(b * factor);
    };
    const isPx = (v: unknown): boolean => typeof v === 'number' || (typeof v === 'string' && !String(v).includes('%'));
    if (isPx(grid['top']) && isPx(grid['bottom'])) {
      scaleAxis('top', 'bottom', Math.round(REF_H * (1 - MIN_PLOT_RATIO)));
    }
    if (isPx(grid['left']) && isPx(grid['right'])) {
      scaleAxis('left', 'right', Math.round(REF_W * (1 - MIN_PLOT_RATIO)));
    }
    return option;
  }

  /** Width (px) of a legend label string at the ~12px legend font. */
  private legendLabelPx(label: string): number {
    return String(label || '').length * 6.5;
  }

  /** For a vertical (left/right) legend, set its width to fit the widest series
   *  name (capped) and widen grid.left/right to clear that band + the axis name,
   *  so long legend labels don't overlap the y-axis name or the plot. MUST mirror
   *  the preview builder. */
  private normalizeLegendSpacing(option: echarts.EChartsOption, style: ChartStyle): echarts.EChartsOption {
    if (!style.showLegend) return option;
    const pos = style.legendPosition;
    if (pos !== 'left' && pos !== 'right') return option;

    const opt = option as Record<string, unknown>;
    const legend = (Array.isArray(opt['legend']) ? opt['legend'][0] : opt['legend']) as Record<string, unknown> | undefined;
    if (!legend) return option;

    const series = (Array.isArray(opt['series']) ? opt['series'] : opt['series'] ? [opt['series']] : []) as Array<Record<string, unknown>>;
    const names = series.map((s) => String(s['name'] ?? '')).filter(Boolean);
    if (!names.length) return option;

    const widest = names.reduce((m, n) => Math.max(m, this.legendLabelPx(n)), 0);
    const legendWidth = Math.min(220, Math.max(96, Math.round(widest + 20)));
    legend['width'] = legendWidth;

    const yAxes = (Array.isArray(opt['yAxis']) ? opt['yAxis'] : opt['yAxis'] ? [opt['yAxis']] : []) as Array<Record<string, unknown>>;
    const sideHasNamedAxis = yAxes.some((a) => {
      if (!a || !a['name']) return false;
      const axisPos = a['position'] || 'left';
      return axisPos === pos;
    });
    const axisRoom = sideHasNamedAxis ? 56 : 0;

    const reserve = 8 + legendWidth + 16 + axisRoom;
    const grid = (opt['grid'] as Record<string, unknown> | undefined) ?? {};
    if (pos === 'left') {
      grid['left'] = Math.max(Number(grid['left'] || 0), reserve);
    } else {
      grid['right'] = Math.max(Number(grid['right'] || 0), reserve);
    }
    opt['grid'] = grid;
    return option;
  }

  /** Centre every axis `name` along its axis with a sensible gap so names sit
   *  clear of the labels instead of overflowing the grid edges. Only touches
   *  axes that actually have a name; leaves everything else untouched. Mirror of
   *  the preview builder's normalizeAxisNames. */
  normalizeAxisNames(option: echarts.EChartsOption): echarts.EChartsOption {
    let hasRightNamedAxis = false;
    let rightAxisLabelPx = 0;
    // Estimate the widest tick label (in px) for each value axis by running its
    // formatter over the series data. The centred axis name sits nameGap px from
    // the axis line; a fixed 52px gap is smaller than wide labels like
    // "700,000%", so the rotated name ("Year"/"Trend") lands on top of the
    // numbers (the published-view overlap). We size the gap from the real labels.
    const valueLabelPxByAxis = this.estimateValueLabelPxByAxis(option);

    // `vertical` = the axis runs top-to-bottom on the left (every y-axis). Its
    // centred name is placed nameGap px to the LEFT, where it can collide with
    // the tick labels — so it needs a larger gap than a horizontal (x) axis,
    // whose name simply sits nameGap px below the labels.
    const apply = (axis: Record<string, unknown> | undefined, vertical: boolean, axisIdx: number): void => {
      if (!axis || !axis['name']) {
        return;
      }
      if (axis['nameLocation'] == null) {
        axis['nameLocation'] = 'middle';
      }
      if (axis['nameGap'] == null) {
        // nameGap is measured from the axis line, and the tick labels extend
        // outward from that same line — so the gap MUST exceed the label width
        // or the centred name overlaps the labels (the bug seen on value axes
        // with wide numbers like "100000"). containLabel:true then absorbs this
        // gap into the auto-sized margin, so a generous gap does NOT create dead
        // space — only a large fixed grid.left does. Category (text) labels are
        // wider, so they need the most clearance.
        if (vertical && axis['type'] === 'value') {
          // Value axis: clear the widest formatted tick label plus padding, with
          // a 52px floor so short labels still get a reasonable gap.
          const widest = valueLabelPxByAxis[axisIdx] ?? 0;
          axis['nameGap'] = Math.max(52, Math.round(widest + 14));
        } else {
          axis['nameGap'] = vertical ? (axis['type'] === 'category' ? 72 : 52) : 30;
        }
      }
      if (axis['nameTextStyle'] == null) {
        axis['nameTextStyle'] = { fontWeight: 600, color: '#475467' };
      }
      if (axis['position'] === 'right') {
        hasRightNamedAxis = true;
        rightAxisLabelPx = Math.max(rightAxisLabelPx, valueLabelPxByAxis[axisIdx] ?? 0);
      }
    };
    const normalize = (a: unknown, vertical: boolean): void => {
      if (Array.isArray(a)) {
        a.forEach((item, idx) => apply(item as Record<string, unknown>, vertical, idx));
      } else if (a) {
        apply(a as Record<string, unknown>, vertical, 0);
      }
    };
    normalize((option as Record<string, unknown>)['xAxis'], false);
    normalize((option as Record<string, unknown>)['yAxis'], true);

    if (hasRightNamedAxis) {
      const grid = (option as Record<string, unknown>)['grid'] as Record<string, unknown> | undefined;
      if (grid) {
        // Reserve room for the right axis name + its widest tick label so the
        // rotated name ("Trend"/"Units") doesn't sit on top of the numbers.
        const reserve = Math.round(64 + rightAxisLabelPx);
        grid['right'] = Math.max(reserve, Number(grid['right'] || 24) + 40);
      }
    }
    return option;
  }

  /** Per-yAxis-index estimate of the widest value-axis tick label in pixels, by
   *  running each value axis's `axisLabel.formatter` over its series data
   *  extremes. Used to size nameGap (left) and grid.right (right) so the centred
   *  axis name never lands on top of wide tick labels (e.g. "700,000%"). */
  private estimateValueLabelPxByAxis(option: echarts.EChartsOption): Record<number, number> {
    const opt = option as Record<string, unknown>;
    const yAxes = Array.isArray(opt['yAxis']) ? (opt['yAxis'] as Record<string, unknown>[]) : [opt['yAxis'] as Record<string, unknown>];
    const series = (Array.isArray(opt['series']) ? opt['series'] : opt['series'] ? [opt['series']] : []) as Array<Record<string, unknown>>;

    const numericFrom = (datum: unknown): number => {
      if (typeof datum === 'number') return datum;
      if (Array.isArray(datum)) return Number(datum[1]);
      if (datum && typeof datum === 'object' && 'value' in (datum as Record<string, unknown>)) {
        return Number((datum as Record<string, unknown>)['value']);
      }
      return Number(datum);
    };

    const result: Record<number, number> = {};
    yAxes.forEach((axis, axisIdx) => {
      if (!axis || axis['type'] !== 'value') return;
      const formatter = (axis['axisLabel'] as Record<string, unknown> | undefined)?.['formatter'];
      const format = typeof formatter === 'function' ? (formatter as (v: number) => string) : (v: number) => String(v);
      const values: number[] = [];
      series
        .filter((s) => (Number(s['yAxisIndex']) || 0) === axisIdx)
        .forEach((s) => {
          (s['data'] as unknown[] | undefined)?.forEach((d) => {
            const n = numericFrom(d);
            if (Number.isFinite(n)) values.push(n);
          });
        });
      if (!values.length) return;
      let widestChars = 0;
      [Math.min(...values), Math.max(...values)].forEach((v) => {
        try {
          widestChars = Math.max(widestChars, String(format(v)).length);
        } catch {
          /* formatter may assume richer params; ignore and keep the floor */
        }
      });
      // ~7px per character at the default ~12px axis-label font.
      result[axisIdx] = widestChars * 7;
    });
    return result;
  }

  /** Bar + line combo with a secondary y-axis. Primary yAxis renders as bars,
   *  yAxis2 (if set) renders as a line — same pattern the DVM combined-chart
   *  components use. */
  buildBarLineOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const y2Key = mapping.yAxis2 || '';
    // Year columns on a value axis must not be summed (2023+2023=4046); collapse
    // additive aggregations to a non-additive one so the value stays a real year.
    const yAggregation = this.resolveValueAggregation(dataset, yKey, aggregation);
    const y2Aggregation = this.resolveValueAggregation(dataset, y2Key, aggregation);
    const palette = this.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);
    const useMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    const hasSecondary = !!y2Key && dataset.columns.some((c) => c.name === y2Key);

    let categories: string[];
    const series: echarts.SeriesOption[] = [];
    // Capture per-category bar totals so we can synthesise a "Total" line on
    // top of the stack when yAxis2 isn't explicitly set. Without this, Mixed
    // Stackbar with auto-default seriesField rendered as just stacked bars.
    let barTotals: number[] | null = null;

    if (useMultiSeries) {
      const grouped = this.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, yAggregation);
      categories = grouped.categories;
      barTotals = grouped.data.map((row) => row.reduce((acc, v) => acc + (Number(v) || 0), 0));
      grouped.seriesNames.forEach((name, idx) => {
        series.push({
          name,
          type: 'bar',
          data: grouped.data.map((row) => row[idx]),
          stack: 'total',
          itemStyle: { color: palette[idx % palette.length] },
          label: labelConfig as unknown as Record<string, unknown>,
        } as echarts.SeriesOption);
      });
    } else {
      const aggregated = this.aggregateByCategory(dataset, xKey, yKey, yAggregation);
      categories = aggregated.map((item) => item.name);
      series.push({
        name: yKey || 'Value',
        type: 'bar',
        data: aggregated.map((item) => item.value),
        itemStyle: { color: palette[0] },
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    }

    if (hasSecondary) {
      const y2Aggregated = this.aggregateByCategory(dataset, xKey, y2Key, y2Aggregation);
      const y2Values = categories.map((cat) => {
        const match = y2Aggregated.find((item) => item.name === cat);
        return match ? match.value : 0;
      });
      series.push({
        name: y2Key,
        type: 'line',
        yAxisIndex: 1,
        data: y2Values,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[(useMultiSeries ? series.length : 1) % palette.length] },
        lineStyle: { width: 2.5 },
        symbol: 'circle',
        symbolSize: 6,
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    } else if (useMultiSeries && barTotals) {
      // No explicit yAxis2 — overlay a "Total" line so the bar-line combo
      // shape is visible (sum across all stack segments per category).
      series.push({
        name: 'Total',
        type: 'line',
        yAxisIndex: 1,
        data: barTotals,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[series.length % palette.length] },
        lineStyle: { width: 2.5 },
        symbol: 'circle',
        symbolSize: 6,
      } as echarts.SeriesOption);
    } else {
      // Single-series fallback — synthesise a running-average trend line so
      // the preview still conveys "bar + line combo" intent.
      const values = (series[0] as { data: number[] }).data;
      const trend = values.map((_, idx) => {
        const window = values.slice(0, idx + 1);
        return window.reduce((acc, v) => acc + (Number(v) || 0), 0) / window.length;
      });
      series.push({
        name: 'Trend',
        type: 'line',
        yAxisIndex: 1,
        data: trend,
        smooth: this.lineSmooth(style),
        itemStyle: { color: palette[1 % palette.length] },
        lineStyle: { width: 2.5, type: 'dashed' },
        symbol: 'circle',
        symbolSize: 6,
      } as echarts.SeriesOption);
    }

    // Secondary X-axis (X2): a second category axis below the primary X,
    // showing the X2 column's value for each primary X category (uses the
    // first row matched per category). Same pattern as buildAxisChartOption.
    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    let xAxisConfig: unknown = { type: 'category', data: categories, name: style.xAxisLabel || xKey || '' };
    if (hasX2) {
      const x2ByCategory = new Map<string, string>();
      dataset.rows.forEach((row) => {
        const cat = String(row[xKey] || '(blank)');
        if (!x2ByCategory.has(cat)) {
          x2ByCategory.set(cat, String(row[x2Key] ?? ''));
        }
      });
      const x2Categories = categories.map((cat) => x2ByCategory.get(cat) ?? '');
      // Lay the two bottom X axes out as distinct labelled rows so their names
      // don't collide: primary labels → primary name → X2 labels → X2 name.
      xAxisConfig = [
        { type: 'category', data: categories, name: style.xAxisLabel || xKey || '', nameLocation: 'middle', nameGap: 16, nameTextStyle: { fontWeight: 600, color: '#475467' } },
        {
          type: 'category',
          data: x2Categories,
          name: style.xAxisLabel2 || x2Key,
          nameLocation: 'middle',
          nameGap: 20,
          nameTextStyle: { fontWeight: 600, color: '#475467' },
          position: 'bottom',
          offset: 38,
          splitLine: { show: false },
          axisTick: { show: true },
          axisLine: { show: true },
        },
      ];
    }

    const formatter = this.buildAxisLabelFormatter(style);
    const formatter2 = this.buildAxisLabelFormatter2(style);
    const gridConfig = this.buildGridConfig(style, false) as Record<string, unknown>;
    if (hasX2) {
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }

    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      tooltip: style.tooltipEnabled
        ? this.buildCustomTooltip(style, 'bar-line', this.buildDualAxisTooltipFormatter(style, dataset, xKey), 'axis', 'cross')
        : { show: false },
      xAxis: xAxisConfig as echarts.EChartsOption['xAxis'],
      yAxis: [
        { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
        { type: 'value', name: style.yAxisLabel2 || y2Key || (useMultiSeries ? 'Total' : 'Trend'), position: 'right', axisLabel: { formatter: formatter2 } },
      ],
      series,
    } as echarts.EChartsOption;
  }

  /** Scatter with size dimension. If `size` mapping is set, point radius
   *  reflects the value; otherwise falls back to fixed-radius bubbles so the
   *  preview still looks like a bubble chart, not a flat scatter. */
  buildBubbleOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || seriesField || '';
    const palette = this.getPaletteColors(style.palette);

    const xValues = dataset.rows.map((row) => this.toNumber(row[xKey]));
    const yValues = dataset.rows.map((row) => this.toNumber(row[yKey]));
    const sizeValues = sizeKey
      ? dataset.rows.map((row) => this.toNumber(row[sizeKey]))
      : [];
    const sizeMax = sizeValues.length ? Math.max(...sizeValues, 1) : 1;
    const minRadius = 8;
    const maxRadius = 36;

    const useGroups = !!colorByKey && dataset.columns.some((c) => c.name === colorByKey);
    const groups = new Map<string, Array<[number, number, number]>>();
    if (useGroups) {
      dataset.rows.forEach((row, idx) => {
        const group = String(row[colorByKey] || '(blank)');
        const point: [number, number, number] = [xValues[idx], yValues[idx], sizeValues[idx] ?? 0];
        const list = groups.get(group) || [];
        list.push(point);
        groups.set(group, list);
      });
    }

    const symbolSize = (value: unknown): number => {
      if (!sizeKey) return minRadius + 6;
      const raw = Array.isArray(value) ? Number(value[2]) : Number(value);
      if (!Number.isFinite(raw) || raw <= 0) return minRadius;
      return minRadius + (raw / sizeMax) * (maxRadius - minRadius);
    };

    const series: echarts.SeriesOption[] = useGroups
      ? Array.from(groups.entries()).map(([name, data], idx) => ({
          name,
          type: 'scatter',
          data,
          symbolSize,
          itemStyle: { color: palette[idx % palette.length], opacity: 0.7 },
        } as echarts.SeriesOption))
      : [{
          name: yKey || 'Series',
          type: 'scatter',
          data: xValues.map((x, i) => [x, yValues[i], sizeValues[i] ?? 0]),
          symbolSize,
          itemStyle: { color: palette[0], opacity: 0.75 },
        } as echarts.SeriesOption];

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: { trigger: 'item' },
      xAxis: { type: 'value', name: style.xAxisLabel || xKey || '', axisLabel: { formatter } },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
      series,
    } as echarts.EChartsOption;
  }

  /** Quadrant Bubble: scatter plot with 4 coloured quadrant backgrounds,
   *  dashed crosshair dividers at data midpoints, and optional bubble sizing. */
  buildQuadrantBubbleOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || seriesField || '';
    const palette = this.getPaletteColors(style.palette);

    // Compute data extent for midpoints and axis padding
    const allXRaw = dataset.rows.map(r => this.toNumber(r[xKey]));
    const allYRaw = dataset.rows.map(r => this.toNumber(r[yKey]));
    const xMin = Math.min(...allXRaw);
    const xMax = Math.max(...allXRaw);
    const yMin = Math.min(...allYRaw);
    const yMax = Math.max(...allYRaw);
    const xPad = (xMax - xMin) * 0.08 || 1;
    const yPad = (yMax - yMin) * 0.08 || 1;
    const xMid = (xMin + xMax) / 2;
    const yMid = (yMin + yMax) / 2;
    const xL = xMin - xPad;
    const xR = xMax + xPad;
    const yB = yMin - yPad;
    const yT = yMax + yPad;

    // Size scaling
    const sizeValues = sizeKey ? dataset.rows.map(r => this.toNumber(r[sizeKey])) : [];
    const sizeMax = sizeValues.length ? Math.max(...sizeValues, 1) : 1;
    const minR = 10;
    const maxR = 40;
    const symbolSize = (value: unknown): number => {
      if (!sizeKey || !sizeValues.length) return 18;
      const raw = Array.isArray(value) ? Number(value[2]) : Number(value);
      if (!Number.isFinite(raw) || raw <= 0) return minR;
      return minR + (raw / sizeMax) * (maxR - minR);
    };

    // Build scatter series grouped by colorBy / seriesField
    let series: echarts.SeriesOption[];
    if (colorByKey && dataset.columns.some(c => c.name === colorByKey)) {
      const grouped = new Map<string, number[][]>();
      dataset.rows.forEach(row => {
        const key = row[colorByKey] || '(blank)';
        const pts = grouped.get(key) || [];
        const pt: number[] = [this.toNumber(row[xKey]), this.toNumber(row[yKey])];
        if (sizeKey) pt.push(this.toNumber(row[sizeKey]));
        pts.push(pt);
        grouped.set(key, pts);
      });
      series = Array.from(grouped.entries()).map(([name, data], idx) => ({
        name,
        type: 'scatter',
        data,
        symbolSize,
        itemStyle: { color: palette[idx % palette.length], opacity: 0.85 },
      } as echarts.SeriesOption));
    } else {
      const points = dataset.rows.map(row => {
        const pt: number[] = [this.toNumber(row[xKey]), this.toNumber(row[yKey])];
        if (sizeKey) pt.push(this.toNumber(row[sizeKey]));
        return pt;
      });
      series = [{ name: yKey || 'Value', type: 'scatter', data: points, symbolSize, itemStyle: { color: palette[0], opacity: 0.85 } } as echarts.SeriesOption];
    }

    // Quadrant background colours — light tints matching reference (BL, BR, TL, TR)
    const Q = [
      'rgba(239,68,68,0.09)',    // bottom-left: light red
      'rgba(245,158,11,0.09)',   // bottom-right: light amber
      'rgba(16,185,129,0.09)',   // top-left: light green
      'rgba(59,130,246,0.09)',   // top-right: light blue
    ];

    // Attach quadrant markArea + crosshair markLine to the first series
    const firstSeries = series[0] as Record<string, unknown>;
    firstSeries['markArea'] = {
      silent: true,
      data: [
        [{ coord: [xL, yB], itemStyle: { color: Q[0] } }, { coord: [xMid, yMid] }],
        [{ coord: [xMid, yB], itemStyle: { color: Q[1] } }, { coord: [xR, yMid] }],
        [{ coord: [xL, yMid], itemStyle: { color: Q[2] } }, { coord: [xMid, yT] }],
        [{ coord: [xMid, yMid], itemStyle: { color: Q[3] } }, { coord: [xR, yT] }],
      ],
    };
    firstSeries['markLine'] = {
      silent: true,
      symbol: ['none', 'none'],
      lineStyle: { color: '#6b7280', type: 'dashed', width: 1.5, opacity: 0.55 },
      label: { show: false },
      data: [{ xAxis: xMid }, { yAxis: yMid }],
    };

    const labelInsetX = (xR - xL) * 0.035;
    const labelInsetY = (yT - yB) * 0.055;
    const quadrantLabels = [
      { name: style.quadrantBottomLeftLabel || '', coord: [xL + labelInsetX, yB + labelInsetY], label: { align: 'left', verticalAlign: 'bottom' } },
      { name: style.quadrantBottomRightLabel || '', coord: [xR - labelInsetX, yB + labelInsetY], label: { align: 'right', verticalAlign: 'bottom' } },
      { name: style.quadrantTopLeftLabel || '', coord: [xL + labelInsetX, yT - labelInsetY], label: { align: 'left', verticalAlign: 'top' } },
      { name: style.quadrantTopRightLabel || '', coord: [xR - labelInsetX, yT - labelInsetY], label: { align: 'right', verticalAlign: 'top' } },
    ].filter((item) => item.name.trim());
    if (quadrantLabels.length) {
      firstSeries['markPoint'] = {
        silent: true,
        symbol: 'circle',
        symbolSize: 0,
        label: {
          show: true,
          formatter: '{b}',
          color: '#344054',
          fontWeight: 700,
          fontSize: 13,
          backgroundColor: 'rgba(255,255,255,0.72)',
          borderColor: 'rgba(148,163,184,0.45)',
          borderWidth: 1,
          borderRadius: 4,
          padding: [4, 8],
        },
        data: quadrantLabels,
      };
    }

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: { trigger: 'item' },
      xAxis: { type: 'value', name: style.xAxisLabel || xKey || '', axisLabel: { formatter }, min: xL, max: xR },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter }, min: yB, max: yT },
      series,
    } as echarts.EChartsOption;
  }

  /** Simple Gantt-style timeline: each row becomes one horizontal bar from
   *  xAxis (start) to yAxis (end / numeric position), grouped by category
   *  (seriesField if present, else xAxis). */
  buildGanttOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const startKey = mapping.xAxis || '';
    const endKey = mapping.yAxis || '';
    const categoryKey = seriesField && dataset.columns.some((c) => c.name === seriesField) ? seriesField : startKey;
    const palette = this.getPaletteColors(style.palette);

    const sameColumn = startKey === endKey;
    const tasks: Array<{ category: string; start: number; end: number; label: string }> = [];
    dataset.rows.forEach((row) => {
      const rawA = this.toNumber(row[startKey]);
      const rawB = this.toNumber(row[endKey]);
      // When the same column is mapped to both axes (e.g., Year?Year), each row
      // gets a 1-unit duration so the Gantt shows a timeline rather than zero bars.
      const start = sameColumn ? rawA : Math.min(rawA, rawB);
      const end = sameColumn ? rawA + 1 : Math.max(rawA, rawB);
      if (end <= start) return;
      tasks.push({
        category: String(row[categoryKey] || '(blank)'),
        start,
        end,
        label: String(row[categoryKey] || ''),
      });
    });

    // If all rows were filtered (e.g., all values are 0), fall back to horizontal bar.
    if (!tasks.length) {
      const catCol = categoryKey !== startKey ? categoryKey : (this.categoryColumns(dataset)[0]?.name || startKey);
      return this.buildAxisChartOption('bar', { xAxis: catCol, yAxis: startKey }, 'sum', dataset, style, '', titleText, { horizontal: true });
    }

    const categories = Array.from(new Set(tasks.map((t) => t.category)));
    // Each "bar" is positioned by start (transparent placeholder) + duration
    // (visible coloured segment) stacked together — standard ECharts Gantt trick.
    const placeholderData: Array<{ value: number; itemStyle: Record<string, unknown> }> = [];
    const durationData: Array<{ value: number; itemStyle: Record<string, unknown> }> = [];
    const yIndexByCategory = new Map<string, number>(categories.map((c, idx) => [c, idx]));

    categories.forEach(() => {
      placeholderData.push({ value: 0, itemStyle: { color: 'transparent' } });
      durationData.push({ value: 0, itemStyle: { color: palette[0] } });
    });

    // For multi-row Gantt, fold all tasks into one row per category by taking
    // earliest start + latest end (a "bar = lifetime of category" preview).
    const aggregatedByCategory = new Map<string, { start: number; end: number }>();
    for (const task of tasks) {
      const existing = aggregatedByCategory.get(task.category);
      if (!existing) {
        aggregatedByCategory.set(task.category, { start: task.start, end: task.end });
      } else {
        existing.start = Math.min(existing.start, task.start);
        existing.end = Math.max(existing.end, task.end);
      }
    }
    aggregatedByCategory.forEach((range, category) => {
      const idx = yIndexByCategory.get(category);
      if (idx === undefined) return;
      placeholderData[idx] = { value: range.start, itemStyle: { color: 'transparent', borderColor: 'transparent' } };
      durationData[idx] = { value: range.end - range.start, itemStyle: { color: palette[idx % palette.length] } };
    });

    // Start the x-axis from the earliest task start so bars are visible even
    // when start values are large numbers (e.g. year 2020). Without this, a
    // bar spanning 2020?2025 is only 5/2500 wide and invisible.
    const allStarts = Array.from(aggregatedByCategory.values()).map(r => r.start);
    const xAxisMin = allStarts.length && Math.min(...allStarts) > 0 ? Math.min(...allStarts) : undefined;

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: { show: false },
      grid: this.buildGridConfig(style, true),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any): string => {
          const list = Array.isArray(params) ? params : [params];
          const visible = list.find((p: any) => p?.seriesName !== 'Start');
          const placeholder = list.find((p: any) => p?.seriesName === 'Start');
          if (!visible) return '';
          const startVal = Number(placeholder?.value ?? 0);
          const endVal = startVal + Number(visible.value ?? 0);
          return `${visible.name}<br/>Start: ${this.formatNumberTooltip(startVal, style)}<br/>End: ${this.formatNumberTooltip(endVal, style)}`;
        },
      },
      xAxis: { type: 'value', min: xAxisMin, name: style.xAxisLabel || startKey || '', axisLabel: { formatter } },
      yAxis: { type: 'category', data: categories, name: style.yAxisLabel || categoryKey || '' },
      series: [
        {
          name: 'Start',
          type: 'bar',
          stack: 'total',
          silent: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
          data: placeholderData,
        },
        {
          name: 'Duration',
          type: 'bar',
          stack: 'total',
          data: durationData,
          label: this.buildSeriesLabel(style) as unknown as Record<string, unknown>,
        },
      ],
    } as echarts.EChartsOption;
  }

  /** ECharts waterfall pattern: stack a transparent placeholder bar (running
   *  pre-balance) with a coloured delta bar so each step shows the cumulative
   *  contribution. Positive and negative deltas use separate visible series so
   *  they can be coloured independently. */
  buildWaterfallOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const palette = this.getPaletteColors(style.palette);
    const positiveColor = palette[0] || '#10b981';
    const negativeColor = palette[1] || palette[0] || '#ef4444';

    const aggregated = this.aggregateByCategory(dataset, xKey, yKey, aggregation);
    const categories = aggregated.map((entry) => entry.name);
    const values = aggregated.map((entry) => Number(entry.value) || 0);

    const placeholder: Array<number | string> = [];
    const positive: Array<number | string> = [];
    const negative: Array<number | string> = [];
    let running = 0;
    for (const value of values) {
      if (value >= 0) {
        placeholder.push(running);
        positive.push(value);
        negative.push('-');
      } else {
        placeholder.push(running + value);
        positive.push('-');
        negative.push(-value);
      }
      running += value;
    }

    const labelConfig = this.buildSeriesLabel(style);
    const valueAxisFormatter = this.buildAxisLabelFormatter(style);

    return {
      title: this.buildTitleConfig(style, titleText),
      legend: { ...this.buildLegendConfig(style), data: ['Increase', 'Decrease'] },
      grid: this.buildGridConfig(style, false),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any): string => {
          const list = Array.isArray(params) ? params : [params];
          const real = list.find((p: any) => p?.seriesName !== 'Placeholder' && p?.value !== '-' && p?.value != null);
          if (!real) return '';
          const raw = Number(real.value);
          return `${real.name}<br/>${real.seriesName}: ${this.formatNumberTooltip(raw, style)}`;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        name: style.xAxisLabel || xKey || '',
      },
      yAxis: {
        type: 'value',
        name: style.yAxisLabel || yKey || '',
        axisLabel: { formatter: valueAxisFormatter },
      },
      series: [
        {
          name: 'Placeholder',
          type: 'bar',
          stack: 'total',
          silent: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
          data: placeholder,
        },
        {
          name: 'Increase',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: positiveColor },
          label: labelConfig as unknown as Record<string, unknown>,
          data: positive,
        },
        {
          name: 'Decrease',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: negativeColor },
          label: labelConfig as unknown as Record<string, unknown>,
          data: negative,
        },
      ],
    } as echarts.EChartsOption;
  }

  /** Waterfall Stack: each series contributes a stacked segment per category,
   *  and the running total across categories creates a waterfall cumulative
   *  effect. Without a seriesField it falls back to a plain waterfall. */
  buildWaterfallStackOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const palette = this.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);

    const hasMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    if (!hasMultiSeries) {
      return this.buildWaterfallOption(mapping, aggregation, dataset, style, titleText);
    }

    const grouped = this.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, aggregation);
    const { categories, seriesNames, data } = grouped;

    // For each category compute the cumulative starting offset across all series.
    // Each bar within a category stacks — plus we add one transparent placeholder
    // per category whose height is the running cumulative total up to that point.
    const categoryTotals = data.map(row => row.reduce((s, v) => s + (Number(v) || 0), 0));
    const placeholderData: number[] = [];
    let runningTotal = 0;
    for (const total of categoryTotals) {
      placeholderData.push(runningTotal);
      runningTotal += total;
    }

    const series: echarts.SeriesOption[] = [
      {
        name: '__placeholder__',
        type: 'bar',
        stack: 'wf',
        silent: true,
        itemStyle: { color: 'transparent', borderColor: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } },
        data: placeholderData,
      } as echarts.SeriesOption,
      ...seriesNames.map((name, idx) => ({
        name,
        type: 'bar' as const,
        stack: 'wf',
        data: data.map(row => row[idx] ?? 0),
        itemStyle: { color: palette[idx % palette.length] },
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption)),
    ];

    const formatter = this.buildAxisLabelFormatter(style);
    return {
      title: this.buildTitleConfig(style, titleText),
      legend: this.buildLegendConfig(style),
      grid: this.buildGridConfig(style, false),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any): string => {
          const list = (Array.isArray(params) ? params : [params]).filter((p: any) => p?.seriesName !== '__placeholder__');
          const name = String(list[0]?.name ?? '');
          const lines = list.map((p: any) => `${p.marker || ''} ${p.seriesName}: ${this.formatNumberTooltip(Number(p.value ?? 0), style)}`);
          return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
        },
      },
      xAxis: { type: 'category', data: categories, name: style.xAxisLabel || xKey || '' },
      yAxis: { type: 'value', name: style.yAxisLabel || yKey || '', axisLabel: { formatter } },
      series,
    } as echarts.EChartsOption;
  }

  /** Whether a title will actually render — explicit override OR the fallback
   *  title text (chart name) passed to buildChartOption. MUST mirror the
   *  preview builder so the published view matches. */
  private hasRenderedTitle(style: ChartStyle): boolean {
    return !!((style.titleOverride || this.currentTitleText || '').trim());
  }

  buildLegendConfig(style: ChartStyle): Record<string, unknown> {
    const base: Record<string, unknown> = { show: style.showLegend, type: 'scroll' };
    // A centred title sits at top:4 (~22px tall). A top legend must clear it,
    // otherwise the two overlap (the reported bug). When there's no title the
    // legend can sit near the very top. MUST mirror the preview builder.
    const hasTitle = this.hasRenderedTitle(style);
    switch (style.legendPosition) {
      case 'top': return { ...base, top: hasTitle ? 30 : 8, left: 'center' };
      // Vertical legends: start BELOW the title (not top:'middle', which centres
      // over the full height and runs the top items up into the title row — the
      // reported header overlap). Cap width so long names don't run under the plot.
      case 'left': return { ...base, left: 8, top: hasTitle ? 34 : 8, orient: 'vertical', width: 96 };
      case 'right': return { ...base, right: 8, top: hasTitle ? 34 : 8, orient: 'vertical', width: 96 };
      case 'bottom':
      default: return { ...base, bottom: 8, left: 'center' };
    }
  }

  buildTitleConfig(style: ChartStyle, fallback: string): Record<string, unknown> {
    const text = (style.titleOverride || fallback || '').trim();
    if (!text) {
      return { show: false };
    }
    return { text, left: 'center', top: 4, textStyle: { fontSize: 14, fontWeight: 600 } };
  }

  buildGridConfig(style: ChartStyle, horizontal: boolean): Record<string, unknown> {
    // containLabel:true reserves room for the tick LABELS only — it does NOT
    // include the rotated axis NAME, which sits nameGap px beyond the axis line
    // and would otherwise be clipped at the card's left edge. So grid.left must
    // hold the part of the name that pokes outside the labels. Keep it just
    // large enough for the name (no more) to avoid the old dead-space gap.
    // Horizontal bar charts use a bigger category-axis nameGap (72 vs 52), so
    // their name needs a little more reserved room.
    // MUST mirror the preview builder's buildGridConfig.
    const hasTitle = this.hasRenderedTitle(style);
    const legendOn = style.showLegend;
    const pos = style.legendPosition;

    let top = hasTitle ? 40 : 32;
    let bottom = 40 + 28;
    let left = horizontal ? 48 : 44;
    let right = 24;

    // Reserve space on the SIDE the legend occupies so it never sits on top of
    // the title, axis labels or the plot (the reported overlap bug).
    if (legendOn) {
      if (pos === 'top') {
        top = (hasTitle ? 56 : 36) + 24;
      } else if (pos === 'bottom') {
        bottom = 56 + 28;
      } else if (pos === 'left') {
        left = Math.max(left, 132);
      } else if (pos === 'right') {
        right = Math.max(right, 132);
      }
    }

    return { left, right, top, bottom, containLabel: true };
  }

  buildAxisLabelFormatter(style: ChartStyle): (value: number) => string {
    // Abbreviated (K/M/B) so a large measure never widens the grid past the plot.
    return (value: number) => this.formatAxisLabel(Number(value), style, false);
  }

  /** Secondary (right) value-axis formatter. MUST mirror the preview builder. */
  buildAxisLabelFormatter2(style: ChartStyle): (value: number) => string {
    return (value: number) => this.formatAxisLabel(Number(value), style, true);
  }

  /** Line series smoothing from the style's lineShape (default curved). */
  private lineSmooth(style: ChartStyle): boolean {
    return style.lineShape !== 'straight';
  }

  /** Axis-aware tooltip formatter for dual-axis charts — right-axis (yAxisIndex
   *  1) series format with the secondary number format. MUST mirror preview. */
  private buildDualAxisTooltipFormatter(
    style: ChartStyle,
    dataset?: UploadedDataset,
    xKey?: string,
  ): (params: unknown) => string {
    const rowsByCategory = this.buildTooltipRowLookup(dataset, xKey);
    return (params: unknown): string => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list = (Array.isArray(params) ? params : [params]) as any[];
      if (!list.length) return '';
      const name = String(list[0]?.axisValueLabel ?? list[0]?.name ?? '');
      const lines = list.map((p: any) => {
        const onSecondary = Number(p?.axisIndex) === 1;
        const raw = Array.isArray(p?.value) ? Number(p.value[1]) : Number(p?.value ?? 0);
        const formatted = this.formatNumberTooltip(raw, style, onSecondary);
        return `${p?.marker || ''} ${p?.seriesName ?? ''}: ${formatted}`;
      });
      this.appendTooltipColumnLines(lines, rowsByCategory, name, xKey, style);
      return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
    };
  }

  /** Map of category value → its (first) source row, for extra tooltip-column
   *  lookups. Empty when no dataset/xKey. */
  private buildTooltipRowLookup(
    dataset?: UploadedDataset,
    xKey?: string,
  ): Map<string, Record<string, string>> {
    const lookup = new Map<string, Record<string, string>>();
    if (!dataset || !xKey) return lookup;
    dataset.rows.forEach((row) => {
      const cat = String(row[xKey] || '(blank)');
      if (!lookup.has(cat)) {
        lookup.set(cat, row);
      }
    });
    return lookup;
  }

  /** Append user-configured extra tooltip columns for the hovered category to an
   *  existing list of tooltip lines (shared by axis + dual-axis tooltips). Each
   *  extra column gets a marker dot + the tooltip's own text color so it reads as
   *  a first-class row alongside the series, not a muted afterthought. */
  private appendTooltipColumnLines(
    lines: string[],
    rowsByCategory: Map<string, Record<string, string>>,
    categoryName: string,
    xKey?: string,
    style?: ChartStyle,
  ): void {
    const tooltipCols = this.shapeOptions?.tooltipColumns;
    if (!tooltipCols?.length || !rowsByCategory.size) return;
    const row = rowsByCategory.get(categoryName);
    if (!row) return;
    const textColor = style?.tooltipTextColor || '#f8fafc';
    const marker = `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background-color:${textColor};opacity:0.55"></span>`;
    tooltipCols.forEach((col) => {
      if (row[col] !== undefined && col !== xKey) {
        lines.push(`${marker}<span style="color:${textColor}">${col}: <strong>${row[col]}</strong></span>`);
      }
    });
  }

  buildSeriesLabel(style: ChartStyle): Record<string, unknown> {
    if (!style.showLabels) {
      return { show: false };
    }
    return {
      show: true,
      position: 'top',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: (params: any): string => {
        const value = params?.value;
        const raw = Array.isArray(value) ? Number(value[1]) : Number(value);
        const formatted = this.formatNumber(raw, style);
        const percent: number | undefined = params?.percent;
        const name: string = String(params?.name ?? '');
        switch (style.labelMode) {
          case 'percent': return percent != null ? `${percent}%` : formatted;
          case 'name-value': return `${name}: ${formatted}`.trim();
          case 'name-percent': return percent != null ? `${name}: ${percent}%` : `${name}: ${formatted}`.trim();
          case 'value':
          default: return formatted;
        }
      },
    };
  }

  buildAxisChartOption(
    seriesType: 'bar' | 'line',
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
    options: { horizontal?: boolean; area?: boolean; stacked?: boolean; step?: boolean } = {},
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    // A year column on the value axis must not be summed (2023+2023=4046); use a
    // non-additive aggregation so the bar height is the actual year.
    const yAggregation = this.resolveValueAggregation(dataset, yKey, aggregation);
    const palette = this.getPaletteColors(style.palette);
    const labelConfig = this.buildSeriesLabel(style);
    const useMultiSeries = !!seriesField && dataset.columns.some((c) => c.name === seriesField) && seriesField !== xKey;
    const stackId = options.stacked ? 'total' : undefined;
    // Step lines connect points with horizontal/vertical segments rather than a
    // sloped line. Only meaningful for line series. Must match the preview
    // builder (self-service-chart-option-builder.service.ts) so the published
    // view is identical to the builder preview.
    const stepOption = options.step && seriesType === 'line' ? { step: 'middle' as const, smooth: false } : {};

    let categories: string[];
    let series: echarts.SeriesOption[];

    if (useMultiSeries) {
      const grouped = this.aggregateByCategoryAndSeries(dataset, xKey, yKey, seriesField, yAggregation);
      categories = grouped.categories;
      series = grouped.seriesNames.map((name, idx) => ({
        name,
        type: seriesType,
        data: grouped.data.map((row) => row[idx]),
        smooth: seriesType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[idx % palette.length] },
        label: labelConfig as unknown as Record<string, unknown>,
        ...(options.area ? { areaStyle: {} } : {}),
        ...(stackId ? { stack: stackId } : {}),
        ...stepOption,
      } as echarts.SeriesOption));
    } else {
      const aggregated = this.aggregateByCategory(dataset, xKey, yKey, yAggregation);
      categories = aggregated.map((item) => item.name);
      const values = aggregated.map((item) => item.value);
      series = [{
        name: yKey || 'Value',
        type: seriesType,
        data: values,
        smooth: seriesType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[0] },
        label: labelConfig as unknown as Record<string, unknown>,
        ...(options.area ? { areaStyle: {} } : {}),
        ...(stackId ? { stack: stackId } : {}),
        ...stepOption,
      } as echarts.SeriesOption];
    }

    // Secondary y-axis series — applied to BOTH single- and multi-series paths
    // so stacked / multi-series charts can also show an extra metric on yAxis2.
    const secondaryYKey = mapping.yAxis2 || '';
    if (secondaryYKey && dataset.columns.some((c) => c.name === secondaryYKey)) {
      const y2Aggregation = this.resolveValueAggregation(dataset, secondaryYKey, aggregation);
      const y2Aggregated = this.aggregateByCategory(dataset, xKey, secondaryYKey, y2Aggregation);
      const y2Values = categories.map((cat) => {
        const match = y2Aggregated.find((item) => item.name === cat);
        return match ? match.value : 0;
      });
      const secondaryType: 'line' | 'bar' = seriesType === 'bar' ? 'line' : 'bar';
      series.push({
        name: secondaryYKey,
        type: secondaryType,
        yAxisIndex: 1,
        data: y2Values,
        smooth: secondaryType === 'line' && this.lineSmooth(style),
        itemStyle: { color: palette[(series.length) % palette.length] },
        lineStyle: secondaryType === 'line' ? { width: 2.5 } : undefined,
        symbol: secondaryType === 'line' ? 'circle' : undefined,
        symbolSize: secondaryType === 'line' ? 6 : undefined,
        label: labelConfig as unknown as Record<string, unknown>,
      } as echarts.SeriesOption);
    }

    const refValue = this.shapeOptions?.referenceValue;
    if (refValue !== undefined && refValue !== null && !options.horizontal && series.length > 0) {
      const firstSeries = series[0] as echarts.SeriesOption & { markLine?: Record<string, unknown> };
      firstSeries.markLine = {
        symbol: 'none',
        data: [{ yAxis: refValue, name: 'Target' }],
        lineStyle: { color: '#ef4444', type: 'dashed', width: 2 },
        label: { formatter: 'Target: {c}' },
      };
    }

    const valueAxis = {
      type: 'value' as const,
      name: style.yAxisLabel || (options.horizontal ? xKey : yKey) || '',
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    const categoryAxis = {
      type: 'category' as const,
      data: categories,
      name: style.xAxisLabel || (options.horizontal ? yKey : xKey) || '',
    };

    const y2Key = mapping.yAxis2 || '';
    const hasY2 = !options.horizontal && !!y2Key && dataset.columns.some((c) => c.name === y2Key);
    const yAxisConfig: unknown = hasY2
      ? [valueAxis, { type: 'value' as const, name: style.yAxisLabel2 || y2Key, position: 'right', axisLabel: { formatter: this.buildAxisLabelFormatter2(style) } }]
      : valueAxis;

    // Secondary X-axis (X2): a second category axis BELOW the primary X axis,
    // showing the X2 column's value for each primary X category (uses the
    // first row matched per category). Lets users pair two labels per bar —
    // e.g. "Region" on the primary axis + "Region code" stacked below it.
    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !options.horizontal && !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    let xAxisConfig: unknown = categoryAxis;
    if (hasX2) {
      const x2ByCategory = new Map<string, string>();
      dataset.rows.forEach((row) => {
        const cat = String(row[xKey] || '(blank)');
        if (!x2ByCategory.has(cat)) {
          x2ByCategory.set(cat, String(row[x2Key] ?? ''));
        }
      });
      const x2Categories = categories.map((cat) => x2ByCategory.get(cat) ?? '');
      // Lay the two bottom X axes out as distinct labelled rows so their names
      // don't collide: primary labels → primary name → X2 labels → X2 name.
      // Set gaps explicitly so normalizeAxisNames (fills only null nameGap)
      // leaves these untouched.
      xAxisConfig = [
        {
          ...(categoryAxis as Record<string, unknown>),
          nameGap: 16,
        },
        {
          type: 'category' as const,
          data: x2Categories,
          name: style.xAxisLabel2 || x2Key,
          nameLocation: 'middle' as const,
          nameGap: 20,
          nameTextStyle: { fontWeight: 600, color: '#475467' },
          position: 'bottom' as const,
          offset: 38, // push it below the primary axis name, not onto it
          splitLine: { show: false },
          axisTick: { show: true },
          axisLine: { show: true },
        },
      ];
    }

    const gridConfig = this.buildGridConfig(style, !!options.horizontal) as Record<string, unknown>;
    if (hasX2) {
      // Reserve room for the full stack: primary labels + primary name + X2
      // labels + X2 name. Without this the lower rows clip at the card edge.
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }

    const tooltipPreviewKind: ChartPreviewKind = options.area ? 'area' : (options.horizontal ? 'horizontal-bar' : (seriesType === 'line' ? 'axis-line' : 'axis-bar'));
    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: hasY2 && style.tooltipEnabled
        ? this.buildCustomTooltip(style, tooltipPreviewKind, this.buildDualAxisTooltipFormatter(style, dataset, xKey), 'axis')
        : this.buildAxisTooltip(style, dataset, xKey, tooltipPreviewKind),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      xAxis: options.horizontal ? valueAxis : (xAxisConfig as echarts.EChartsOption['xAxis']),
      yAxis: options.horizontal ? categoryAxis : (yAxisConfig as echarts.EChartsOption['yAxis']),
      series,
    };
  }

  resolveTooltipTrigger(style: ChartStyle, previewKind: ChartPreviewKind): 'axis' | 'item' {
    if (style.tooltipTrigger !== 'auto') {
      return style.tooltipTrigger;
    }
    const axisKinds: ChartPreviewKind[] = ['axis-bar', 'axis-line', 'horizontal-bar', 'area'];
    return axisKinds.includes(previewKind) ? 'axis' : 'item';
  }

  buildTooltipConfig(
    style: ChartStyle,
    previewKind: ChartPreviewKind,
    formatter?: (params: unknown) => string,
  ): Record<string, unknown> {
    if (!style.tooltipEnabled) {
      return { show: false };
    }
    const trigger = this.resolveTooltipTrigger(style, previewKind);
    const config: Record<string, unknown> = {
      trigger,
      show: true,
      backgroundColor: style.tooltipBackground,
      borderColor: style.tooltipBorderColor,
      borderWidth: 1,
      borderRadius: style.tooltipBorderRadius,
      padding: [8, 12],
      textStyle: { color: style.tooltipTextColor, fontSize: style.tooltipFontSize },
      extraCssText: `box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);`,
    };
    if (trigger === 'axis') {
      config['axisPointer'] = style.tooltipAxisPointer === 'none'
        ? { show: false }
        : { type: style.tooltipAxisPointer };
    }
    if (formatter) {
      config['formatter'] = formatter;
    }
    return config;
  }

  /** Styled tooltip that can force a trigger / axis-pointer (for dual-axis combo
   *  charts) while honouring tooltipEnabled + styling. MUST mirror the preview. */
  buildCustomTooltip(
    style: ChartStyle,
    previewKind: ChartPreviewKind,
    formatter?: (params: unknown) => string,
    forcedTrigger?: 'axis' | 'item',
    axisPointer?: 'shadow' | 'cross' | 'line',
  ): Record<string, unknown> {
    if (!style.tooltipEnabled) {
      return { show: false };
    }
    const config = this.buildTooltipConfig(style, previewKind, formatter);
    if (forcedTrigger) {
      config['trigger'] = forcedTrigger;
    }
    if ((forcedTrigger === 'axis' || config['trigger'] === 'axis') && axisPointer && style.tooltipAxisPointer === 'line') {
      config['axisPointer'] = { type: axisPointer };
    }
    return config;
  }

  buildAxisTooltip(
    style: ChartStyle,
    dataset: UploadedDataset,
    xKey: string,
    previewKind: ChartPreviewKind,
  ): Record<string, unknown> {
    const tooltipCols = this.shapeOptions?.tooltipColumns;
    if (!tooltipCols?.length || !xKey) {
      return this.buildTooltipConfig(style, previewKind);
    }
    const rowsByCategory = this.buildTooltipRowLookup(dataset, xKey);
    const formatter = (params: unknown): string => {
      const arr = Array.isArray(params) ? params : [params];
      const first = arr[0] as { name?: string };
      const name = String(first?.name ?? '');
      const lines = arr.map((p) => {
        const item = p as { marker?: string; seriesName?: string; value?: number };
        return `${item.marker || ''} ${item.seriesName || ''}: ${this.formatNumberTooltip(Number(item.value ?? 0), style)}`;
      });
      this.appendTooltipColumnLines(lines, rowsByCategory, name, xKey, style);
      return `<strong>${name}</strong><br/>${lines.join('<br/>')}`;
    };
    return this.buildTooltipConfig(style, previewKind, formatter);
  }

  buildTreemapOption(
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const categoryKey = mapping.category || '';
    const valueKey = mapping.value || '';
    const aggregated = this.aggregateByCategory(dataset, categoryKey, valueKey, aggregation);
    const palette = this.getPaletteColors(style.palette);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const labelFormatter = (params: any): string => {
      const value = this.formatNumber(Number(params?.value), style);
      const name = String(params?.name ?? '');
      return style.showLabels ? `${name}\n${value}` : name;
    };

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, 'treemap', (params: unknown) => {
        const p = params as { name?: string; value?: number };
        return `${String(p?.name ?? '')}: ${this.formatNumberTooltip(Number(p?.value), style)}`;
      }),
      series: [{
        type: 'treemap',
        roam: false,
        data: aggregated,
        label: { show: true, formatter: labelFormatter },
      }] as unknown as echarts.SeriesOption[],
    };
  }

  buildPieOption(
    isDonut: boolean,
    mapping: Partial<Record<MappingKey, string>>,
    aggregation: AggregationType,
    dataset: UploadedDataset,
    style: ChartStyle,
    titleText: string,
  ): echarts.EChartsOption {
    const categoryKey = mapping.category || '';
    const valueKey = mapping.value || '';
    const aggregated = this.aggregateByCategory(dataset, categoryKey, valueKey, aggregation);
    const palette = this.getPaletteColors(style.palette);

    const labelConfig: Record<string, unknown> = style.showLabels
      ? {
          show: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (params: any): string => {
            const formatted = this.formatNumber(Number(params?.value), style);
            const name = String(params?.name ?? '');
            const percent = params?.percent ?? 0;
            switch (style.labelMode) {
              case 'percent': return `${percent}%`;
              case 'name-value': return `${name}: ${formatted}`;
              case 'name-percent': return `${name}: ${percent}%`;
              case 'value':
              default: return formatted;
            }
          },
        }
      : { show: false };

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, isDonut ? 'donut' : 'pie', (params: unknown) => {
        const p = params as { name?: string; value?: number; percent?: number };
        return `${String(p?.name ?? '')}: ${this.formatNumberTooltip(Number(p?.value), style)} (${p?.percent ?? 0}%)`;
      }),
      legend: this.buildLegendConfig(style),
      series: [{
        type: 'pie',
        radius: isDonut ? ['45%', '70%'] : '70%',
        center: ['50%', '50%'],
        data: aggregated,
        label: labelConfig,
      }] as unknown as echarts.SeriesOption[],
    };
  }

  buildScatterOption(
    mapping: Partial<Record<MappingKey, string>>,
    dataset: UploadedDataset,
    style: ChartStyle,
    seriesField: string,
    titleText: string,
  ): echarts.EChartsOption {
    const xKey = mapping.xAxis || '';
    const yKey = mapping.yAxis || '';
    const sizeKey = mapping.size || '';
    const colorByKey = mapping.colorBy || '';
    const palette = this.getPaletteColors(style.palette);
    const groupingKey = colorByKey || seriesField;
    const useMultiSeries = !!groupingKey && dataset.columns.some((c) => c.name === groupingKey);
    const hasSize = !!sizeKey && dataset.columns.some((c) => c.name === sizeKey);

    let sizeMin = Infinity;
    let sizeMax = -Infinity;
    if (hasSize) {
      dataset.rows.forEach((row) => {
        const v = this.toNumber(row[sizeKey]);
        if (v < sizeMin) sizeMin = v;
        if (v > sizeMax) sizeMax = v;
      });
      if (sizeMin === Infinity) { sizeMin = 0; sizeMax = 1; }
      if (sizeMin === sizeMax) sizeMax = sizeMin + 1;
    }
    const scaleSize = (v: number): number => {
      if (!hasSize) return 10;
      const ratio = (v - sizeMin) / (sizeMax - sizeMin);
      return 8 + ratio * 32;
    };

    let series: echarts.SeriesOption[];
    if (useMultiSeries) {
      const grouped = new Map<string, number[][]>();
      dataset.rows.forEach((row) => {
        const key = row[groupingKey] || '(blank)';
        const points = grouped.get(key) || [];
        const pt: number[] = [this.toNumber(row[xKey]), this.toNumber(row[yKey])];
        if (hasSize) pt.push(this.toNumber(row[sizeKey]));
        points.push(pt);
        grouped.set(key, points);
      });
      series = Array.from(grouped.entries()).map(([name, points], idx) => ({
        name,
        type: 'scatter',
        data: points,
        symbolSize: hasSize
          ? ((val: unknown) => scaleSize((val as number[])[2] ?? 0))
          : 10,
        itemStyle: { color: palette[idx % palette.length] },
      } as echarts.SeriesOption));
    } else {
      const points = dataset.rows.map((row) => {
        const pt: number[] = [this.toNumber(row[xKey]), this.toNumber(row[yKey])];
        if (hasSize) pt.push(this.toNumber(row[sizeKey]));
        return pt;
      });
      series = [{
        name: yKey || 'Value',
        type: 'scatter',
        data: points,
        symbolSize: hasSize
          ? ((val: unknown) => scaleSize((val as number[])[2] ?? 0))
          : 10,
        itemStyle: { color: palette[0] },
      } as echarts.SeriesOption];
    }

    const x2Key = mapping.xAxis2 || '';
    const hasX2 = !!x2Key && dataset.columns.some((c) => c.name === x2Key);
    const primaryXAxis = {
      type: 'value' as const,
      name: style.xAxisLabel || xKey,
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    let x2Min = Infinity;
    let x2Max = -Infinity;
    if (hasX2) {
      dataset.rows.forEach((row) => {
        const v = this.toNumber(row[x2Key]);
        if (Number.isFinite(v)) {
          if (v < x2Min) x2Min = v;
          if (v > x2Max) x2Max = v;
        }
      });
      if (x2Min === Infinity) { x2Min = 0; x2Max = 1; }
      if (x2Min === x2Max) x2Max = x2Min + 1;
    }
    const xAxisConfig: unknown = hasX2
      ? [
          // Two stacked bottom X axes: lay out as distinct labelled rows so the
          // names don't collide (primary labels → primary name → X2 labels → X2 name).
          { ...primaryXAxis, nameLocation: 'middle' as const, nameGap: 16, nameTextStyle: { fontWeight: 600, color: '#475467' } },
          {
            type: 'value' as const,
            name: style.xAxisLabel2 || x2Key,
            nameLocation: 'middle' as const,
            nameGap: 20,
            nameTextStyle: { fontWeight: 600, color: '#475467' },
            position: 'bottom' as const,
            offset: 38,
            min: x2Min,
            max: x2Max,
            axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
            splitLine: { show: false },
          },
        ]
      : primaryXAxis;

    // Secondary Y-axis (Y2) for scatter — second numeric value axis on the
    // right with its own scale derived from the Y2 column range.
    const y2Key = mapping.yAxis2 || '';
    const hasY2 = !!y2Key && dataset.columns.some((c) => c.name === y2Key);
    const primaryYAxis = {
      type: 'value' as const,
      name: style.yAxisLabel || yKey,
      axisLabel: { formatter: this.buildAxisLabelFormatter(style) },
    };
    let y2Min = Infinity;
    let y2Max = -Infinity;
    if (hasY2) {
      dataset.rows.forEach((row) => {
        const v = this.toNumber(row[y2Key]);
        if (Number.isFinite(v)) {
          if (v < y2Min) y2Min = v;
          if (v > y2Max) y2Max = v;
        }
      });
      if (y2Min === Infinity) { y2Min = 0; y2Max = 1; }
      if (y2Min === y2Max) y2Max = y2Min + 1;
    }
    const yAxisConfig: unknown = hasY2
      ? [
          primaryYAxis,
          {
            type: 'value' as const,
            name: style.yAxisLabel2 || y2Key,
            position: 'right' as const,
            min: y2Min,
            max: y2Max,
            axisLabel: { formatter: this.buildAxisLabelFormatter2(style) },
            splitLine: { show: false },
          },
        ]
      : primaryYAxis;

    const gridConfig = this.buildGridConfig(style, false) as Record<string, unknown>;
    if (hasX2) {
      // X2 sits as a second labelled row below the primary X-axis name; reserve
      // room for primary labels + primary name + X2 labels + X2 name.
      gridConfig['bottom'] = Math.max(80, Number(gridConfig['bottom'] || 40) + 44);
    }
    if (hasY2) {
      gridConfig['right'] = Math.max(56, Number(gridConfig['right'] || 24) + 32);
    }

    return {
      color: palette,
      title: this.buildTitleConfig(style, titleText),
      tooltip: this.buildTooltipConfig(style, 'scatter'),
      legend: this.buildLegendConfig(style),
      grid: gridConfig,
      xAxis: xAxisConfig as echarts.EChartsOption['xAxis'],
      yAxis: yAxisConfig as echarts.EChartsOption['yAxis'],
      series,
    };
  }

  aggregateByCategoryAndSeries(
    dataset: UploadedDataset,
    categoryKey: string,
    valueKey: string,
    seriesKey: string,
    aggregation: AggregationType,
  ): { categories: string[]; seriesNames: string[]; data: number[][] } {
    const categories: string[] = [];
    const seriesNames: string[] = [];
    const categoryIndex = new Map<string, number>();
    const seriesIndex = new Map<string, number>();
    const buckets = new Map<string, Record<string, string>[]>();

    for (const row of dataset.rows) {
      const cat = row[categoryKey] || '(blank)';
      const ser = row[seriesKey] || '(blank)';
      if (!categoryIndex.has(cat)) {
        categoryIndex.set(cat, categories.length);
        categories.push(cat);
      }
      if (!seriesIndex.has(ser)) {
        seriesIndex.set(ser, seriesNames.length);
        seriesNames.push(ser);
      }
      // Bucket by category+series INDICES (not a joined string) so labels that
      // themselves contain the separator — e.g. "North America", "Q1 2023" —
      // don't get mis-split when we read the bucket back.
      const ci = categoryIndex.get(cat) ?? 0;
      const si = seriesIndex.get(ser) ?? 0;
      const bucketKey = `${ci}|${si}`;
      const list = buckets.get(bucketKey) || [];
      list.push(row);
      buckets.set(bucketKey, list);
    }

    const data: number[][] = categories.map(() => new Array(seriesNames.length).fill(0));
    for (const [bucketKey, rows] of buckets.entries()) {
      const [ciStr, siStr] = bucketKey.split('|');
      const ci = Number(ciStr);
      const si = Number(siStr);
      const aggregated = this.aggregateByCategory(
        { ...dataset, rows },
        categoryKey,
        valueKey,
        aggregation,
      );
      data[ci][si] = aggregated[0]?.value ?? 0;
    }

    return { categories, seriesNames, data };
  }

  /** Converts a numeric timeline value to an ISO-8601 date string for the
   *  DVM gantt-chart component, which parses "YYYY-MM-DD" natively.
   *  Year-like values (1900–2100) → Jan 1 of that year.
   *  Large values (>1e10) → treated as ms-timestamp.
   *  Other positive values → treated as seconds-timestamp. */
  toGanttDateString(value: number): string {
    if (Number.isFinite(value) && value >= 1900 && value <= 2100) {
      return `${Math.round(value)}-01-01`;
    }
    if (value > 1e10) {
      return new Date(value).toISOString().slice(0, 10);
    }
    if (value > 0) {
      return new Date(value * 1000).toISOString().slice(0, 10);
    }
    return `${new Date().getFullYear()}-01-01`;
  }

  toNumber(value: string | undefined): number {
    const normalized = String(value || '').replace(/,/g, '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  aggregateByCategory(
    dataset: UploadedDataset,
    categoryKey: string,
    valueKey: string,
    aggregation: AggregationType,
  ): Array<{ name: string; value: number }> {
    dataset = this.applyDateGranularityToDataset(dataset, categoryKey);
    const custom = this.isCustomAggregation(aggregation) ? this.getCustomAggregation(aggregation) : undefined;

    if (custom) {
      const groupedRows = new Map<string, Record<string, string>[]>();
      dataset.rows.forEach((row) => {
        const category = row[categoryKey] || '(blank)';
        const list = groupedRows.get(category) || [];
        list.push(row);
        groupedRows.set(category, list);
      });
      try {
        const evaluator = this.buildAggregationEvaluator(custom.expression, dataset.columns.map((c) => c.name));
        return this.applyShapeToAggregated(
          Array.from(groupedRows.entries()).map(([name, rows]) => {
            const result = Number(evaluator(rows));
            return { name, value: Number.isFinite(result) ? result : 0 };
          }),
        );
      } catch {
        return this.applyShapeToAggregated(
          Array.from(groupedRows.entries()).map(([name]) => ({ name, value: 0 })),
        );
      }
    }

    const grouped = new Map<string, number[]>();
    dataset.rows.forEach((row) => {
      const category = row[categoryKey] || '(blank)';
      const values = grouped.get(category) || [];
      values.push(this.toNumber(row[valueKey]));
      grouped.set(category, values);
    });

    return this.applyShapeToAggregated(
      Array.from(grouped.entries()).map(([name, values]) => ({
        name,
        value: this.aggregateValues(values, aggregation),
      })),
    );
  }

  aggregateValues(values: number[], aggregation: AggregationType): number {
    if (aggregation === 'count') {
      return values.length;
    }
    if (aggregation === 'distinctCount') {
      return new Set(values).size;
    }
    if (!values.length) {
      return 0;
    }
    if (aggregation === 'avg') {
      return values.reduce((acc, value) => acc + value, 0) / values.length;
    }
    if (aggregation === 'min') {
      return Math.min(...values);
    }
    if (aggregation === 'max') {
      return Math.max(...values);
    }
    if (aggregation === 'range') {
      return Math.max(...values) - Math.min(...values);
    }
    if (aggregation === 'first') {
      return values[0];
    }
    if (aggregation === 'last') {
      return values[values.length - 1];
    }
    if (aggregation === 'median' || aggregation === 'p25' || aggregation === 'p75' || aggregation === 'p90' || aggregation === 'p95') {
      const percentile = aggregation === 'median' ? 50
        : aggregation === 'p25' ? 25
        : aggregation === 'p75' ? 75
        : aggregation === 'p90' ? 90
        : 95;
      const sorted = [...values].sort((a, b) => a - b);
      const rank = (percentile / 100) * (sorted.length - 1);
      const lo = Math.floor(rank);
      const hi = Math.ceil(rank);
      return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
    }
    return values.reduce((acc, value) => acc + value, 0);
  }

  getCustomAggregation(key: string): CustomAggregation | undefined {
    return this.deps.customAggregations.find((agg) => agg.key === key);
  }

  isCustomAggregation(value: AggregationType): boolean {
    return !this.deps.builtinAggregations.some((agg) => agg.value === value);
  }

  substituteColumnRefs(expression: string, columnNames: string[], replacer: (name: string) => string): string {
    let result = expression.replace(/\[([^\]]+)\]/g, (_, name) => replacer(name.trim()));
    const sortedNames = [...columnNames].sort((a, b) => b.length - a.length);
    for (const name of sortedNames) {
      if (/[^A-Za-z0-9_]/.test(name)) continue;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`(?<![\\w'"\\$])${escaped}(?![\\w'"(])`, 'g');
      result = result.replace(pattern, replacer(name));
    }
    return result;
  }

  normalizeLogicalOps(expression: string): string {
    return expression.replace(/\bAND\b/gi, '&&').replace(/\bOR\b/gi, '||').replace(/\bNOT\b/gi, '!');
  }

  buildRowEvaluator(expression: string, columnNames: string[]): (row: Record<string, string>) => unknown {
    const substituted = this.substituteColumnRefs(expression, columnNames, (name) => `__num(__row[${JSON.stringify(name)}])`);
    const normalized = this.normalizeLogicalOps(substituted);
    const body = `
      const __num = (v) => { const s = String(v ?? '').replace(/,/g, '').trim(); const n = Number(s); return Number.isFinite(n) ? n : (s === '' ? 0 : s); };
      const IF = (c, t, e) => (c ? t : e);
      const concat = (...a) => a.map((x) => String(x ?? '')).join('');
      const upper = (s) => String(s ?? '').toUpperCase();
      const lower = (s) => String(s ?? '').toLowerCase();
      const trim = (s) => String(s ?? '').trim();
      const length = (s) => String(s ?? '').length;
      const substr = (s, start, end) => String(s ?? '').substring(start, end);
      const replace = (s, find, repl) => String(s ?? '').split(String(find)).join(String(repl));
      const contains = (s, sub) => String(s ?? '').includes(String(sub));
      const startsWith = (s, p) => String(s ?? '').startsWith(String(p));
      const endsWith = (s, p) => String(s ?? '').endsWith(String(p));
      const year = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getFullYear(); };
      const month = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getMonth() + 1; };
      const day = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getDate(); };
      const dayOfWeek = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getDay(); };
      const abs = Math.abs, round = Math.round, ceil = Math.ceil, floor = Math.floor;
      const sqrt = Math.sqrt, log = Math.log, exp = Math.exp, pow = Math.pow;
      const min = (...a) => Math.min(...a), max = (...a) => Math.max(...a);
      return (${normalized});
    `;
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function('__row', body) as (row: Record<string, string>) => unknown;
  }

  buildAggregationEvaluator(expression: string, columnNames: string[]): (rows: Record<string, string>[]) => number {
    const substituted = this.substituteColumnRefs(expression, columnNames, (name) => `__col(${JSON.stringify(name)})`);
    const normalized = this.normalizeLogicalOps(substituted);
    const body = `
      const __num = (v) => { const s = String(v ?? '').replace(/,/g, '').trim(); const n = Number(s); return Number.isFinite(n) ? n : 0; };
      const __col = (name) => __rows.map((r) => __num(r[name]));
      const sum = (a) => a.reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);
      const avg = (a) => a.length ? sum(a) / a.length : 0;
      const count = (a) => Array.isArray(a) ? a.length : __rows.length;
      const distinctCount = (a) => new Set(a).size;
      const min = (a) => a.length ? Math.min(...a) : 0;
      const max = (a) => a.length ? Math.max(...a) : 0;
      const median = (a) => percentile(a, 50);
      const percentile = (a, p) => {
        if (!a.length) return 0;
        const s = [...a].sort((x, y) => x - y);
        const rank = (p / 100) * (s.length - 1);
        const lo = Math.floor(rank), hi = Math.ceil(rank);
        return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (rank - lo);
      };
      const first = (a) => a[0];
      const last = (a) => a[a.length - 1];
      const range = (a) => max(a) - min(a);
      const abs = Math.abs, round = Math.round, ceil = Math.ceil, floor = Math.floor;
      const sqrt = Math.sqrt, log = Math.log, exp = Math.exp, pow = Math.pow;
      const IF = (c, t, e) => (c ? t : e);
      return Number(${normalized});
    `;
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function('__rows', body) as (rows: Record<string, string>[]) => number;
  }

  /** Equivalent of the source component's `categoryColumns` getter. The Gantt
   *  fallback path reads `this.categoryColumns[0]?.name`; in the component this
   *  derives from the live `this.dataset`, but here the dataset is always passed
   *  explicitly, so we compute it from the same source. */
  private categoryColumns(dataset: UploadedDataset): DatasetColumn[] {
    const source = dataset?.columns;
    if (!source) return [];
    return source.filter((column) => column.type !== 'empty');
  }
}



